import { ogImageMeta } from "@/lib/seo/og-image";
import { useLoaderData } from "@tanstack/react-router";
import { LocaleLink } from "@/components/site/LocaleLink";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PostCard } from "@/components/insights/PostCard";
import { Pagination } from "@/components/insights/Pagination";
import { Reveal } from "@/components/site/Reveal";
import { listPosts, listCategories } from "@/lib/wp.functions";
import type { WpCategory, WpPost } from "@/server/wp.server";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { useT } from "@/lib/i18n/I18nProvider";

import { blogListSchemas } from "@/lib/seo/page-schema";
import { urlFor } from "@/lib/seo/schema";


export function BlogIndex() {
  const { locale } = useT();
  const isBg = locale === "bg";
  const { posts, cats, page, category } = useLoaderData({ strict: false }) as { posts: { posts: WpPost[]; totalPages: number; total: number; error: string | null }; cats: { categories: WpCategory[]; error: string | null }; page: number; category?: string };
  const decodeName = (s: string) =>
    s
      .replace(/&amp;/g, "&")
      .replace(/&#038;/g, "&")
      .replace(/&#8211;/g, "-")
      .replace(/&#8217;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");
  const seen = new Set<string>();
  const uniqueCats = (cats.categories as WpCategory[])
    .map((c) => ({ ...c, name: decodeName(c.name) }))
    .filter((c) => {
      if (c.name.trim().toLowerCase() === "статии") return false;
      const key = c.name.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[1200px] px-6 py-16 lg:px-10 lg:py-24">
        <Reveal>
          <Breadcrumbs
            className="mb-6"
            items={[
              { label: isBg ? "Начало" : "Home", to: "/" },
              { label: isBg ? "Блог" : "Blog" },
            ]}
          />
          <span className="inline-flex items-center rounded-full border border-border/70 bg-secondary/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {isBg ? "Блог" : "Blog library"}
          </span>
          <h1 className="font-display mt-5 text-[clamp(2rem,3.6vw,3.2rem)] font-medium text-foreground">
            {isBg ? "Истории, анализи и продуктови новини." : "Stories, analysis and product updates."}
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-foreground/70">
            {isBg
              ? "Практически анализи и експертни гледни точки за медиен мониторинг, медиен анализ, репутация на брандове и управление на кризи - от екипа зад Perceptica."
              : "Practical thinking on media intelligence, brand reputation and crisis response - from the team building Perceptica."}
          </p>
        </Reveal>

        {uniqueCats.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            <LocaleLink
              to="/blog"
              search={{ page: 1 }}
              className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                !category ? "border-foreground bg-foreground text-background" : "border-border/60 hover:bg-secondary"
              }`}
            >
              {isBg ? "Всички" : "All"}
            </LocaleLink>
            {uniqueCats.slice(0, 12).map((c) => (
              <LocaleLink
                key={c.id}
                to="/blog"
                search={{ page: 1, category: c.slug }}
                className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  category === c.slug
                    ? "border-foreground bg-foreground text-background"
                    : "border-border/60 hover:bg-secondary"
                }`}
              >
                {c.name}
              </LocaleLink>
            ))}
          </div>
        )}

        {posts.error && (
          <p className="mt-12 rounded-xl border border-border/60 bg-secondary/40 p-6 text-[14px] text-foreground/70">
            {isBg
              ? "В момента не успяваме да заредим публикациите. Моля, опитайте отново след малко."
              : "We couldn't load posts right now. Please try again shortly."}
          </p>
        )}

        {!posts.error && posts.posts.length === 0 && (
          <p className="mt-12 text-[14px] text-muted-foreground">
            {isBg ? "Няма намерени публикации." : "No posts found."}
          </p>
        )}

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(posts.posts as WpPost[]).map((p) => (
            <PostCard key={p.slug} post={p} />
          ))}
        </div>

        <Pagination page={page} totalPages={posts.totalPages} category={category} />
      </main>
      <SiteFooter />
    </div>
  );
}
